import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentResult, AssessmentSettings, Course, ExperienceType, LearnspaceUserRole } from '@shared/models';
import { AssessmentService } from '../../shared/services/assessment.service';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { CourseAnalyticsTab } from '@shared/services/analytics.service';
import { environment } from '@env/environment';
import { InternalAnalyticsService } from '@shared/services/internalAnalytics.service';
import { getRoomUrl } from '@shared/utils/url-builder.utils';
import { EMPTY } from 'rxjs';
import { catchError, share, switchMap, take, tap } from 'rxjs/operators';
import { getAssessmentDisplayParams } from '@shared/utils/assessment.utils';

enum Error {
  'default' = 'default',
  'rateLimit' = 'rateLimit'
}

@Component({
  selector: 'learner-report',
  templateUrl: './learner-report.component.html',
  styleUrls: ['./learner-report.component.scss']
})
export class LearnerReportComponent implements OnInit {
  @Input() inputCourseId: string | null = null;
  @Input() inputxApiSessionId: string | null = null;
  @Input() inputAssessmentResult: AssessmentResult | null = null;
  @Input() fromEditorContext: boolean = false;
  @Output() onRestart = new EventEmitter<void>();

  public course?: Course;
  public originalCourseId?: string;
  public isLastSimulation: boolean = false;
  public canEdit: boolean = false;
  public hasClickedAssessmentButton: boolean = false;
  public assessmentSettings?: AssessmentSettings;
  public assessmentResult?: AssessmentResult;
  public error?: Error;
  public ltiReturnEnabled: boolean = false;
  private disableContinue?: boolean = false;
  private isLti?: boolean = false;

  private _roomUrlRoot: string;

  constructor(
    private _courseService: CoursesAPIService,
    private _assessmentService: AssessmentService,
    private _authenticationService: AuthenticationService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _internalAnalyticsService: InternalAnalyticsService
  ) {}

  ngOnInit(): void {
    if (this.inputCourseId && this.inputxApiSessionId) {
      this._initFromInputs();
    } else {
      this._initFromRoute();
    }
  }

  private async _initFromInputs() {
    this.disableContinue = true;
    const wondaUser = await this._authenticationService.getWondaUser();
    this.hasClickedAssessmentButton = wondaUser ? wondaUser.feature_usage?.hasClickedAssessmentButton : true;
    this.loadData(this.inputCourseId, this.inputxApiSessionId, this.inputAssessmentResult);
  }

  private _initFromRoute() {
    if (window.parent) {
      this._internalAnalyticsService.hideChat();
    }

    this.originalCourseId = this._route.snapshot.queryParams['originalCourseId'];
    this.isLti = this._route.snapshot.queryParams['isLti'] === 'true';
    this.disableContinue = this.isLti;
    this.isLastSimulation = this._route.snapshot.queryParams['isLastSimulation'] === 'true';

    this._route.paramMap.subscribe(async (params) => {
      const wondaUser = await this._authenticationService.getWondaUser();
      this.hasClickedAssessmentButton = wondaUser ? wondaUser.feature_usage?.hasClickedAssessmentButton : true;

      const courseId = params.get('courseId');
      const sessionId = params.get('sessionId');
      this.loadData(courseId, sessionId);
    });
  }

  private loadData(courseId: string, sessionId: string, assessmentResult?: AssessmentResult) {
    const course$ = this._courseService.getCourseViewLazy(courseId).pipe(share());

    course$.subscribe((course) => {
      this.course = course;
      this.assessmentSettings = this.course?.information?.assessment;
      this._roomUrlRoot = getRoomUrl(this.course.experience_type);
      this.canEdit = course.learnspace.lsRole === LearnspaceUserRole.contributor;
      this.ltiReturnEnabled = this.isLti && !!course.learnspace?.hub?.properties?.enableLTIReturnURLTypeLink;
    });

    if (assessmentResult) {
      // Already fetched by the caller (program-launcher, via the learnspace sessions route) — skip the redundant assessment lookup.
      course$.pipe(take(1)).subscribe(() => {
        this.assessmentResult = assessmentResult;
        this.onAssessmentReady();
      });
      return;
    }

    course$
      .pipe(
        take(1),
        switchMap(() =>
          this._assessmentService.getAssessmentBySessionId(sessionId).pipe(
            catchError((e) => {
              if (e.status !== 404) {
                this.error = Error.default;
                return EMPTY;
              }
              const creatorId = this._authenticationService.wondaUser ? this._authenticationService.wondaUser.id : undefined;
              return this._assessmentService.generate({ sessionId, courseId, creatorId, trigger: 'learner-view' }).pipe(
                catchError((e) => {
                  this.error = e.status === 429 ? Error.rateLimit : Error.default;
                  return EMPTY;
                })
              );
            })
          )
        ),
        tap((assessmentResult) => {
          this.assessmentResult = assessmentResult;
          this.onAssessmentReady();
        })
      )
      .subscribe();
  }
  onAssessmentReady() {
    if (this.inputCourseId) return;
    if (window.parent) {
      const assessmentReportUrl = new URL(window.location.href);
      if (!assessmentReportUrl.searchParams.has('disableContinue')) {
        assessmentReportUrl.searchParams.append('disableContinue', 'true');
      }
      const scoreMax = this.assessmentSettings.maxScore || 5;
      const { score } = getAssessmentDisplayParams(this.assessmentResult, this.assessmentSettings);
      window.parent.postMessage({ event: 'assessmentReady', url: assessmentReportUrl.toString(), score, scoreMax }, this._roomUrlRoot);
    }
  }
  restart() {
    if (this.inputCourseId) {
      this.onRestart.emit();
      return;
    }
    // If assessment report is embedded in the player
    if (window.parent) {
      window.parent.postMessage({ event: 'restartExperience' }, this._roomUrlRoot);
    } else {
      history.back();
    }
  }

  returnToLms() {
    if (window.parent) {
      window.parent.postMessage({ event: 'returnToLms' }, this._roomUrlRoot);
    }
  }

  async openAssessmentDashboard(editingMode: boolean = false) {
    await this.updateFeatureUsageIfNeeded();

    if (editingMode && this.course?.experience_type === ExperienceType.ai) {
      // In this case, the assessment should be edited in the simplified editor.
      // Otherwise, it should be edited in the legacy dialog in the dashboard view.
      const url = `${this.course.learnspace.hub.urlTitle}/${this.course.learnspace_id}/simulations/${this.course.id}`;
      if (window.parent) {
        window.parent.postMessage({ event: 'navigateToEditor', url: `${url}?step=5` }, this._roomUrlRoot);
        return;
      }
      this._router.navigate([url], { queryParams: { step: 5 } });
      return;
    }
    const url = `analytics/${this.course.learnspace.hub.urlTitle}/${this.course.learnspace_id}/courses/${this.course.id}`;
    // If report is embedded, redirect the parent level frame to assessment dashboard or edition url
    if (window.parent) {
      window.parent.postMessage({ event: 'navigateToEditor', url: `${url}?activeTab=${CourseAnalyticsTab.Assessment}${editingMode ? '#editor' : undefined}` }, this._roomUrlRoot);
      return;
    }
    this._router.navigate([url], {
      queryParams: {
        activeTab: CourseAnalyticsTab.Assessment
      },
      fragment: editingMode ? 'editor' : undefined
    });
  }

  openProgramIndex() {
    const courseId = this.originalCourseId || this.course.id;
    const url = `player-view/programs/${this.course.learnspace_id}/launcher`;
    this._router.navigate([url], { queryParams: { courseId } }); // do no share sessionId to include it in the state of the program launcher
  }

  private async updateFeatureUsageIfNeeded() {
    if (this.hasClickedAssessmentButton) return;

    const feature_usage = { hasClickedAssessmentButton: true };
    await this._authenticationService.updateMyUser({ feature_usage }).toPromise();
  }
}
